import useAuth from '@app/auth/use-auth';
import PageErrorFallback from '@app/components/layout/page/components/page-error-fallback';
import { ErrorBoundary } from '@sentry/react';
import { useGetCompanyReport } from '@shared/api/hooks/ccf/reports';
import { CompanyReportDetail } from '@shared/api/types';
import { createContext, useCallback, useContext, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';

export type TContext = CompanyReportDetail & {
  reportStatus?: 'error' | 'success' | 'loading';
  scope3Supported: boolean;
  setCompanyReportPolling: (enabled: boolean) => void;
};

const Context = createContext<TContext | null>(null);

export function useCCF() {
  return useContext(Context) as TContext;
}

export default function CCFContext() {
  const params = useParams();
  const { user } = useAuth();
  const companyReportUuid = params.report_uuid;

  const [poll, setPoll] = useState<boolean>(false);

  const { data: companyReport, status: companyReportStatus } =
    useGetCompanyReport(companyReportUuid!, {
      polling: poll,
      pollingInterval: 1000,
      enabled: !!companyReportUuid,
    });

  const setCompanyReportPolling = useCallback((enabled: boolean) => {
    setPoll(enabled);
  }, []);

  const context: TContext = {
    reportStatus: companyReportStatus,
    ...companyReport!,
    categories: companyReport?.categories as CompanyReportDetail['categories'],
    scope3Supported: user!.features.scope3,
    setCompanyReportPolling,
  };

  return (
    <>
      <ErrorBoundary fallback={<PageErrorFallback />}>
        <Context.Provider value={context}>
          <Outlet />
        </Context.Provider>
      </ErrorBoundary>
    </>
  );
}
